import { queryOptions } from '@tanstack/react-query';

import { apiGammaTeamFetch, apiUserFetch } from '@/api';
import { cleanParams } from '@/utils/cleanParams';

import {
  ActivityDayResponse,
  GAMMA_TEAM_QUERY_KEYS,
  GammaTeamMember,
  GetActivityDayParams,
  GetActivityParams,
  GetGammaTeamDashboardParams,
  GetGammaTeamDashboardRes,
  MemberActivityResponse,
  MyGammaTeamInvitation,
  TeamActivityResponse,
  TeamGoalsResponse,
} from './types';

const DASHBOARD_MAX_PAGE_SIZE = 100;

const fetchGammaTeamDashboard = (params: GetGammaTeamDashboardParams) =>
  apiGammaTeamFetch<GetGammaTeamDashboardRes>('/dashboard', {
    params: {
      page: params.page,
      limit: params.limit,
      ...(params.search && { search: params.search }),
      ...(params.role && { role: params.role }),
      ...(params.status && { status: params.status }),
    },
  });

export const _getGammaTeamDashboard = (params: GetGammaTeamDashboardParams) =>
  queryOptions({
    queryKey: [GAMMA_TEAM_QUERY_KEYS.dashboard, params],
    queryFn: () => fetchGammaTeamDashboard(params),
  });

export const _getAllGammaTeamMembers = (params: Pick<GetGammaTeamDashboardParams, 'search'>) =>
  queryOptions({
    queryKey: [GAMMA_TEAM_QUERY_KEYS.dashboard, 'all', params],
    queryFn: async () => {
      const rows: GammaTeamMember[] = [];
      let totalCount = Infinity;

      for (let page = 1; rows.length < totalCount; page += 1) {
        const response = await fetchGammaTeamDashboard({
          ...params,
          page,
          limit: DASHBOARD_MAX_PAGE_SIZE,
        });
        if (response.data.length === 0) break;

        rows.push(...response.data);
        totalCount = response.totalCount;
      }

      return rows;
    },
  });

export const _getMyGammaTeamInvitations = () =>
  queryOptions({
    queryKey: [GAMMA_TEAM_QUERY_KEYS.myInvitations],
    queryFn: () => apiUserFetch<MyGammaTeamInvitation[]>('/invitations/my'),
    refetchOnMount: true,
  });

export const _getTeamActivity = (params: GetActivityParams) =>
  queryOptions({
    queryKey: [GAMMA_TEAM_QUERY_KEYS.activity, params],
    queryFn: () =>
      apiGammaTeamFetch<TeamActivityResponse>('/activity', { params: cleanParams(params) }),
  });

export const _getActivityDay = (params: GetActivityDayParams) =>
  queryOptions({
    queryKey: [GAMMA_TEAM_QUERY_KEYS.activityDay, params],
    queryFn: () =>
      apiGammaTeamFetch<ActivityDayResponse>('/activity/day', { params: cleanParams(params) }),
  });

export const _getTeamGoals = () =>
  queryOptions({
    queryKey: [GAMMA_TEAM_QUERY_KEYS.goals],
    queryFn: () => apiGammaTeamFetch<TeamGoalsResponse>('/goals'),
  });

export const _getMemberActivity = (userId: string, params: GetActivityParams) =>
  queryOptions({
    queryKey: [GAMMA_TEAM_QUERY_KEYS.memberActivity, userId, params],
    queryFn: () =>
      apiGammaTeamFetch<MemberActivityResponse>(`/activity/members/${userId}`, {
        params: cleanParams(params),
      }),
  });
